/**
 * 家族（兄弟）の利用可否の取得（GET /api/family-membership）。ルーターの入口と Top で共有する（指示書 B）。
 *
 * - 同時に呼ばれたら、通信は1回（実行中の取得を共有）。
 * - 「利用できる子がいる」結果だけを、同じユーザーで短い時間（10秒）再利用する（入口 → Top の読み込み）。
 *   「利用できる子がいない」結果・失敗は再利用しない（体験の開始・決済の直後に、古い結果で止めないため）。
 *   契約の再開・体験の開始・学習者の追加の後は、呼び出し側で invalidateFamilyMembership() を呼んで捨てる。
 * - ログアウト・別のユーザーへの切り替え・invalidate で、それより前に始まった取得の結果は、
 *   保存もせず、待っていた呼び出し元にも返さない（今の状態で取り直す。1回まで）。
 * - 後から始まった取得（再試行の force など）があるときは、古い取得の結果を保存せず、待っていた呼び出し元には新しい結果を返す。
 * - ルーター・Top の期限切れでは、実行中の取得を手放す（abandon）。手放した取得は、後から届いても保存にも
 *   呼び出し元への返却にも使わない（次の呼び出し・回復画面の再試行は、新しい要求を出す）。
 * - 開始から8秒以上たった実行中の取得は、その後の呼び出しでは共有しない（新しい要求を出す）。
 *   通信を自動で中止するものではない。
 * - 取得に失敗したら { kind: 'error' }。未契約とは扱わない。
 */

import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { classifyFamilyResponse } from '../utils/familyMembership.js'

export const FAMILY_REUSE_MS = 10000
export const FAMILY_STALE_IN_FLIGHT_MS = 8000

let generation = 0
let requestSeq = 0
let cache = null // { userId, at, result, generation }
let inFlight = null // { userId, promise, startedAt, generation, seq, abandoned }
// 最後に始めた取得の結果（保存しない「利用できない」結果も含む）。古い取得を待っていた呼び出し元に渡す
let latest = null // { userId, seq, generation, result }

const STATE_CHANGED = { kind: 'error', reason: 'state_changed' }
const ABANDONED = { kind: 'error', reason: 'abandoned' }

function invalidateAll() {
  generation += 1
  cache = null
  inFlight = null
  latest = null
}

supabase.auth.onAuthStateChange((event, session) => {
  const nextUserId = session?.user?.id || null
  if (event === 'SIGNED_OUT' || !nextUserId || (cache && cache.userId !== nextUserId) || (inFlight && inFlight.userId !== nextUserId)) {
    invalidateAll()
  }
})

async function fetchFamily(token) {
  const base = getApiBase()
  const res = await fetch(`${base ? base + '/' : ''}api/family-membership`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  const json = res.ok ? await res.json().catch(() => null) : null
  return classifyFamilyResponse({ ok: res.ok, json })
}

function startFetch(userId, token) {
  const gen = generation
  const seq = ++requestSeq
  const entry = { userId, promise: null, startedAt: Date.now(), generation: gen, seq, abandoned: false }
  entry.promise = (async () => {
    let result
    try {
      result = await fetchFamily(token)
    } catch (e) {
      result = { kind: 'error' }
    }
    if (inFlight === entry) inFlight = null
    // 無効化より前・手放した・後から始まった取得がある古い取得の結果は、保存しない
    if (!entry.abandoned && gen === generation && seq === requestSeq) {
      latest = { userId, seq, generation: gen, result }
      if (result.kind === 'ok' && result.family.any_access) {
        cache = { userId, at: Date.now(), result, generation: gen }
      } else if (cache && cache.userId === userId) {
        cache = null
      }
    }
    return result
  })()
  inFlight = entry
  return entry
}

/**
 * 呼び出し元へ渡す時点で、結果がまだ使えるかを確かめる。
 * - 無効化（ログアウト・ユーザー変更・invalidate）より前に始まった取得 → 今の状態で取り直す（1回まで。それでも古ければ取得できなかった扱い）
 * - 後から始まった取得がある → その新しい結果を返す
 * - 手放した（abandon）取得 → その結果は返さない（新しい取得があればその結果、無ければ取得できなかった扱い）
 */
async function deliver(entry, retried) {
  const result = await entry.promise
  if (entry.generation !== generation) {
    return retried ? STATE_CHANGED : getFamilyMembership({ force: true, _retried: true })
  }
  if (entry.abandoned || entry.seq !== requestSeq) {
    if (inFlight && inFlight.seq > entry.seq) return deliver(inFlight, retried)
    if (latest && latest.seq > entry.seq && latest.generation === generation && latest.userId === entry.userId) return latest.result
    if (entry.abandoned) return ABANDONED
  }
  return result
}

/**
 * @param {{ force?: boolean }} [opts] force: 再試行のとき（再利用しない）
 * @returns {Promise<{ kind: 'ok', family: object } | { kind: 'error' } | { kind: 'no_session' }>}
 */
export async function getFamilyMembership({ force = false, _retried = false } = {}) {
  const gen = generation
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const userId = session?.user?.id
  if (!token || !userId) return { kind: 'no_session' }
  // セッションを読んでいる間に無効化された（ログアウトなど）
  if (gen !== generation) return _retried ? STATE_CHANGED : getFamilyMembership({ force: true, _retried: true })

  if (!force && cache && cache.userId === userId && cache.generation === generation && Date.now() - cache.at < FAMILY_REUSE_MS) {
    // 再利用した結果であることを示す（呼び出し側は「この子は利用できない」と判断する前に取り直す）
    return { ...cache.result, fromCache: true }
  }
  const reusable = !force && inFlight && inFlight.userId === userId && inFlight.generation === generation &&
    Date.now() - inFlight.startedAt < FAMILY_STALE_IN_FLIGHT_MS
  const entry = reusable ? inFlight : startFetch(userId, token)
  return deliver(entry, _retried)
}

/**
 * 実行中の取得を手放す（ルーター・Top の読み込みが期限を過ぎたとき）。
 * 次の呼び出しは新しく取得する。手放した取得が後から届いても、保存にも呼び出し元への返却にも使わない
 */
export function abandonFamilyMembershipInFlight() {
  if (inFlight) inFlight.abandoned = true
  inFlight = null
}

/** 結果を捨てる（契約の再開・体験の開始・学習者の追加などの直後）。それより前に始まった取得の結果も使わない */
export function invalidateFamilyMembership() {
  invalidateAll()
}
